import type { ImportResult, ProcessedReport } from '@integratio/core';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

export interface HistoryEntry {
  reportId: string;
  fileName: string;
  generatedAt: string;
  totalStakeholders: number;
  user: string;
}

export interface AuthSession {
  authenticated: boolean;
  username?: string;
}

export class ApiError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(response: Response): Promise<never> {
  let code = `HTTP_${response.status}`;
  let message = 'Não foi possível concluir a operação. Tente novamente.';
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string } };
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) message = body.error.message;
  } catch {
    /* resposta sem JSON */
  }
  throw new ApiError(code, message);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...init });
  } catch {
    throw new ApiError('NETWORK_ERROR', 'Falha de conexão com o servidor. Verifique se a API está no ar.');
  }
  if (!response.ok) await parseError(response);
  return (await response.json()) as T;
}

export interface ColumnsMetadata {
  expectedColumns: string[];
  maxUploadBytes: number;
}

export const api = {
  async login(username: string, password: string): Promise<AuthSession> {
    return request<AuthSession>('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  },

  async logout(): Promise<void> {
    const response = await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    if (!response.ok) await parseError(response);
  },

  async getSession(): Promise<AuthSession> {
    return request<AuthSession>('/auth/session');
  },

  async getColumnsMetadata(): Promise<ColumnsMetadata> {
    return request<ColumnsMetadata>('/metadata/columns');
  },

  async uploadSpreadsheet(file: File): Promise<ImportResult> {
    const body = new FormData();
    body.append('file', file);
    return request<ImportResult>('/imports', { method: 'POST', body });
  },

  async generateReport(importId: string): Promise<ProcessedReport> {
    return request<ProcessedReport>('/reports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ importId, confirmed: true }),
    });
  },

  async getReport(reportId: string): Promise<ProcessedReport> {
    return request<ProcessedReport>(`/reports/${reportId}`);
  },

  async listHistory(): Promise<HistoryEntry[]> {
    return request<HistoryEntry[]>('/reports');
  },

  pdfUrl(reportId: string, chart?: string): string {
    const query = chart ? `?chart=${encodeURIComponent(chart)}` : '';
    return `${API_BASE}/reports/${reportId}/pdf${query}`;
  },

  pptxUrl(reportId: string): string {
    return `${API_BASE}/reports/${reportId}/pptx`;
  },

  pngUrl(reportId: string): string {
    return `${API_BASE}/reports/${reportId}/png`;
  },

  async downloadPdf(reportId: string, chart?: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await fetch(this.pdfUrl(reportId, chart), { credentials: 'include' });
    if (!response.ok) await parseError(response);
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await response.blob(), fileName: match?.[1] ?? `integratio-relatorio.pdf` };
  },

  async downloadPptx(reportId: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await fetch(this.pptxUrl(reportId), { credentials: 'include' });
    if (!response.ok) await parseError(response);
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await response.blob(), fileName: match?.[1] ?? 'integratio-relatorio-completo.pptx' };
  },

  async downloadPngPack(reportId: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await fetch(this.pngUrl(reportId), { credentials: 'include' });
    if (!response.ok) await parseError(response);
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await response.blob(), fileName: match?.[1] ?? 'integratio-graficos.zip' };
  },
};
